import { MenuIcon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { paths } from '@/app/router/paths';
import DocumentIcon from '@/shared/assets/document.svg?react';
import ForecastIcon from '@/shared/assets/forecast.svg?react';
import HomeIcon from '@/shared/assets/home.svg?react';
import Insights from '@/shared/assets/insights.svg?react';
import NotificationIcon from '@/shared/assets/notification.svg?react';
import PartnerIcon from '@/shared/assets/partner.svg?react';
import ProjectIcon from '@/shared/assets/project.svg?react';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Link } from '@/shared/components/ui/link';
import { CurrentUserButton } from '@/shared/components/ui/nav-bar/current-user-button';
import { NavigationItem } from '@/shared/components/ui/nav-bar/navigation-item';
import { OrganisationButton } from '@/shared/components/ui/nav-bar/organisation-button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import { useIsMobile } from '@/shared/hooks/use-mobile';
import { cn } from '@/shared/utils/cn';

interface NavbarProps {
  className?: string;
  children?: React.ReactNode;
}

// Navigation route type
interface NavigationRoute {
  title: string;
  url: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// Constants
const DESKTOP_SIDEBAR_WIDTH = 70;
const DesktopNavigation = ({ routes }: { routes: NavigationRoute[] }) => {
  const location = useLocation();

  return (
    <div className="fixed left-0 top-0 z-30 hidden h-full md:block">
      <div className="flex h-full items-start pl-10 pt-10">
        <Card
          className={`bg-transparent bg-gradient-to-b ${Gradients.transparentWhiteGradient} flex h-min min-w-[70px] flex-col items-center justify-between rounded-full border border-white/[0.16] pb-5 pl-2 pr-2 pt-5`}
        >
          <div className="flex w-full flex-col items-center gap-4">
            <div className="mb-2">
              <OrganisationButton />
            </div>

            <nav className="flex flex-col items-center gap-3">
              {routes.map((route) => (
                <NavigationItem
                  key={route.url}
                  icon={route.icon}
                  label={route.title}
                  href={route.url}
                  isSelected={location.pathname === route.url}
                />
              ))}
            </nav>
          </div>

          <div className="mt-8">
            <CurrentUserButton />
          </div>
        </Card>
      </div>
    </div>
  );
};
// Mobile Navigation Item Component
const MobileNavigationItem = ({
  route,
  isSelected,
  onNavigate,
}: {
  route: NavigationRoute;
  isSelected: boolean;
  onNavigate: () => void;
}) => (
  <Link
    to={route.url}
    onClick={onNavigate}
    className={cn(
      'flex items-center gap-3 rounded-lg px-4 py-3 text-white transition-all duration-200 hover:bg-white/10',
      isSelected && 'bg-white/20 shadow-sm',
    )}
  >
    <route.icon className="size-5 shrink-0" />
    <span className="text-sm font-medium">{route.title}</span>
  </Link>
);
const MobileNavigation = ({ routes }: { routes: NavigationRoute[] }) => {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const handleNavigate = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      {/* Mobile header with menu button */}
      <div className="from-slate-900/95 to-slate-800/95 fixed inset-x-0 top-0 z-40 h-16 border-b border-white/10 bg-gradient-to-r backdrop-blur-md md:hidden">
        <div className="flex h-full items-center justify-between px-4">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                btnType="icon"
                className="h-10 w-10 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm transition-all duration-200 hover:bg-white/20"
              >
                <MenuIcon className="size-5 text-white" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="from-slate-900/98 to-slate-800/98 border-slate-700/50 w-[280px] bg-gradient-to-b backdrop-blur-md sm:w-[320px]"
            >
              <SheetHeader className="pb-6">
                <SheetTitle className="text-left">
                  <OrganisationButton />
                </SheetTitle>
              </SheetHeader>

              <nav className="flex flex-col gap-2 py-4">
                {routes.map((route) => (
                  <MobileNavigationItem
                    key={route.url}
                    route={route}
                    isSelected={location.pathname === route.url}
                    onNavigate={handleNavigate}
                  />
                ))}
              </nav>

              <div className="border-slate-700/50 mt-8 space-y-4 border-t pt-6">
                <div className="flex justify-center">
                  <CurrentUserButton />
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <OrganisationButton />
          </div>
        </div>
      </div>

      {/* Mobile content spacer */}
      <div className="h-16 md:hidden" />
    </>
  );
};

const Navbar = React.forwardRef<HTMLDivElement, NavbarProps>(
  ({ className, children }, ref) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation('common');

    const routes = [
      {
        title: t('navbar.overview'),
        url: paths.dashboard.path,
        icon: HomeIcon,
      },
      {
        title: t('navbar.projects'),
        url: paths.projects.hub.path,
        icon: ProjectIcon,
      },
      {
        title: t('navbar.insights'),
        url: paths.insights.path,
        icon: Insights,
      },
      {
        title: t('navbar.forecast'),
        url: paths.forecast.path,
        icon: ForecastIcon,
      },
      {
        title: t('navbar.documents'),
        url: paths.documents.path,
        icon: DocumentIcon,
      },
      {
        title: t('navbar.partners'),
        url: paths.partners.hub.path,
        icon: PartnerIcon,
      },
      {
        title: t('navbar.messages'),
        url: paths.notifications.path,
        icon: NotificationIcon,
      },
    ];

    return (
      <div
        ref={ref}
        className={cn(
          'relative w-full',
          isMobile ? 'min-h-screen' : 'h-screen',
          className,
        )}
      >
        {isMobile ? (
          <>
            <MobileNavigation routes={routes} />
            <main className="flex min-h-0 flex-1 flex-col">{children}</main>
          </>
        ) : (
          <>
            <DesktopNavigation routes={routes} />
            <main
              className="flex min-h-screen flex-1 flex-col"
              style={{ marginLeft: `${DESKTOP_SIDEBAR_WIDTH + 60}px` }}
            >
              {children}
            </main>
          </>
        )}
      </div>
    );
  },
);

Navbar.displayName = 'Navbar';

const NavbarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'main'>
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip bg-transparent px-4 pt-10 md:px-8 lg:px-10',
        className,
      )}
      {...props}
    />
  );
});

NavbarInset.displayName = 'NavbarInset';

export { Navbar, NavbarInset };
